import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faSpinner, faPlus, faArrowLeft, faTrash, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch, formatDateTime } from '../../utils/http';

interface Topic {
  id: string;
  title: string;
  authorName: string;
  createdAt: string;
  postCount: number;
  lastActivityAt: string;
}

interface Post {
  id: string;
  authorName: string;
  authorRole: string;
  body: string;
  createdAt: string;
}

interface TopicDetail {
  id: string;
  title: string;
  authorName: string;
  createdAt: string;
  canPost: boolean;
  canManage: boolean;
  posts: Post[];
}

interface Props {
  learnershipId: string;
  /** Moderators are read-only. */
  readOnly?: boolean;
}

export default function DiscussionsPanel({ learnershipId, readOnly = false }: Props) {
  const { accessToken } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [detail, setDetail] = useState<TopicDetail | null>(null);
  const [reply, setReply] = useState('');

  const loadTopics = useCallback(async () => {
    setIsLoading(true);
    const res = await authFetch<Topic[]>(accessToken, `/api/community/courses/${learnershipId}/topics`);
    if (res.ok && res.data) {
      setTopics(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load discussions.');
    }
    setIsLoading(false);
  }, [accessToken, learnershipId]);

  useEffect(() => {
    setDetail(null);
    loadTopics();
  }, [loadTopics]);

  const openTopic = async (id: string) => {
    const res = await authFetch<TopicDetail>(accessToken, `/api/community/topics/${id}`);
    if (res.ok && res.data) setDetail(res.data);
    else setError(res.message || 'Failed to open topic.');
  };

  const createTopic = async () => {
    setError('');
    if (!newTitle.trim() || !newBody.trim()) {
      setError('Title and message are required.');
      return;
    }
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/community/courses/${learnershipId}/topics`, {
      method: 'POST',
      body: { title: newTitle, body: newBody },
    });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setNewTitle('');
    setNewBody('');
    setShowNew(false);
    loadTopics();
  };

  const sendReply = async () => {
    if (!detail || !reply.trim()) return;
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/community/topics/${detail.id}/posts`, {
      method: 'POST',
      body: { body: reply },
    });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setReply('');
    openTopic(detail.id);
  };

  const deleteTopic = async () => {
    if (!detail || !window.confirm('Delete this topic and all replies?')) return;
    const res = await authFetch(accessToken, `/api/community/topics/${detail.id}`, { method: 'DELETE' });
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setDetail(null);
    loadTopics();
  };

  if (detail) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setDetail(null);
              loadTopics();
            }}
            className="flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
            All topics
          </button>
          {detail.canManage && (
            <button onClick={deleteTopic} className="px-3 py-1.5 text-red-600 text-xs font-semibold border border-red-200 rounded-lg hover:bg-red-50 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
              Delete topic
            </button>
          )}
        </div>

        <div className="card-surface p-5">
          <h3 className="text-lg font-bold text-brand-950">{detail.title}</h3>
          <p className="text-xs text-brand-900/50 mt-0.5">
            Started by {detail.authorName} · {formatDateTime(detail.createdAt)}
          </p>
        </div>

        {detail.posts.map((p) => (
          <div key={p.id} className="card-surface p-5">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-semibold text-brand-950">{p.authorName}</span>
              {p.authorRole && p.authorRole !== 'Student' && (
                <span className="px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold uppercase">{p.authorRole === 'Instructor' ? 'Trainer' : p.authorRole}</span>
              )}
              <span className="text-xs text-brand-900/40">{formatDateTime(p.createdAt)}</span>
            </div>
            <p className="text-sm text-brand-900/80 whitespace-pre-wrap">{p.body}</p>
          </div>
        ))}

        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {detail.canPost && !readOnly && (
          <div className="card-surface p-5 space-y-3">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              rows={3}
              placeholder="Write a reply"
              className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
            <div className="flex justify-end">
              <button
                onClick={sendReply}
                disabled={isSaving || !reply.trim()}
                className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isSaving ? faSpinner : faPaperPlane} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                Reply
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowNew((v) => !v)}
            className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            New topic
          </button>
        </div>
      )}

      {showNew && !readOnly && (
        <div className="card-surface p-5 space-y-3">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Topic title"
            className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <textarea
            value={newBody}
            onChange={(e) => setNewBody(e.target.value)}
            rows={3}
            placeholder="Start the conversation"
            className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <div className="flex justify-end">
            <button
              onClick={createTopic}
              disabled={isSaving}
              className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
            >
              <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
              Create topic
            </button>
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
        </div>
      ) : topics.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <FontAwesomeIcon icon={faComments} className="h-12 w-12 text-brand-300 mb-4" />
          <p className="text-brand-900/60">No discussions yet.</p>
        </div>
      ) : (
        <div className="card-surface divide-y divide-canvas-200 overflow-hidden">
          {topics.map((t) => (
            <button key={t.id} onClick={() => openTopic(t.id)} className="w-full text-left px-5 py-4 hover:bg-canvas-50 transition-colors flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-brand-950 truncate">{t.title}</p>
                <p className="text-xs text-brand-900/50">
                  {t.authorName} · last activity {formatDateTime(t.lastActivityAt)}
                </p>
              </div>
              <span className="text-xs font-semibold text-brand-700 bg-brand-50 px-2.5 py-1 rounded-full flex-shrink-0">
                {t.postCount} {t.postCount === 1 ? 'post' : 'posts'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
